import { defaultTheme } from '@digitalnatives/css-variables';

/**
 * Grid
 * ================
 * This grid should be used to set up the layouts of your application
 *
 *  https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/layout/grid.js
 */
const grid = {
    ...defaultTheme.grid,

    // The side-gap specified the spacing between the browser window and the application layout
    'side-gap': {
        xs: '1.25rem',
        sm: '2rem',
        md: '3rem',
        lg: '4rem',
        xl: '5rem',
        xxl: '7.5rem'
    },

    // The grid gap specifies the spacing between areas in your grid layout
    gap: {
        xs: '1.25rem',
        sm: '1.5rem',
        md: '2rem',
        lg: '2rem',
        xl: '2rem',
        xxl: '2.5rem'
    },

    // The maxWidth specifies the maximum widths of the layout components
    maxWidth: {
        page: '75rem',
        wideContent: '62.25rem',
        content: '50rem'
    }
};

/**
 * Spacings
 * ================
 * These spacings should be applied between block- and inline-block level elements in your application.
 *
 * You should avoid using many per-component custom spacings to give your apps a consistent look.
 *
 * It is recommended to expand on these spacings with re-usable variants in your theme vars instead.
 * Per-component spacings should then only be applied as a matter of last resort.
 *
 * https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/layout/spacing.js
 */
const spacing = {
    ...defaultTheme.spacing,

    '2xs': '.25rem',
    xs: '.5rem',
    sm: '.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '2.5rem',
    '3xl': '3rem',
    '4xl': '4.125rem',
};

/**
 * https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/styles/boxShadow.js
 */
const boxShadow = {
    ...defaultTheme.boxShadow,

    light: '0px 0px 8px rgba(0, 0, 0, 0.06)',
    base: '0px 0px 6px rgba(0, 0, 0, 0.16)',
    medium: '0px 0px 11px rgba(0, 0, 0, 0.20)',
    heavy: '0px 0px 40px rgba(0, 0, 0, 0.24)',
    outline: '0px 0px 12px rgba(0, 0, 0, 0.24)',
    inset: 'inset 1px 1px 4px rgba(0, 0, 0, 0.12)'
};

/**
 * Colors
 * ====================
 * Individual themes may have more or less colours than included here, but these are the base colours
 * which the toolkit may use.
 *
 * It is recommended to keep these colours and override them with a different colour in your app, even if you don't use
 * the colours within your own theme, to prevent unthemed parts of the toolkit.
 *
 * These colours are all prefixed with `--color`.
 * https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/styles/color.js
 */

const color = {
    ...defaultTheme.color,

    primary: 'var(--color-brown)',
    brown: '#923C3B',
    blue: '#006789',
    red: '#BA4356',
    green: '#004F41',
    beige: '#FCFAE9',
    mint: '#E6F8F4',
    sky: '#E8F7FF',
    peach: '#FFEADE',
    fog: '#FFFEFC',
    sand: '#E4D7C8',
    gold: '#C7A353',
    'focus-ring': 'var(--color-brown)',

    slate: '#22292A',
    lightest: '#FFFFFF',
    darkest: '#22292A',

    gray: {
        100: '#EEF2F5',
        200: '#9797AA',
        300: '#6B6B7F',
    },

    'highlight-success': '#64DCBF',
    'highlight-success-light': '#cafff2',
    'highlight-error': '#DC6464',
    'highlight-error-light': '#ffd0d0',
    'highlight-info': '#8897FF',
    'highlight-info-light': '#e8f7ff'
};

/**
 * Transitions
 * ================
 * Use these transitions to modify your projects' animations.
 *
 * Even though you may diverge for specific components, these transitions may be used by
 * toolkit components and thus should not be removed.
 *
 * In order to improve performance, these transitions do NOT include the properties to transition,
 * as "all" should be avoided whenever possible.
 *
 * https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/styles/transition.js
 */

const transition = {
    ...defaultTheme.transition,

    page: 'opacity var(--transition-timing-fast) var(--transition-timingFunction-out)',
    link: 'color var(--transition-fast)'
};

/**
 * https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/typography/font.js
 */
const font = {
    ...defaultTheme.font,

    family: {
        primary: ['Raleway', 'sans-serif'],
        secondary: ['Larken-Regular', 'serif']
    },

    size: {
        xs: '.75rem',
        sm: '.875rem',
        md: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '2rem',
        '4xl': '2.25rem',
        '5xl': '2.5rem',
        '6xl': '3rem',
        '7xl': '5rem',
        '8xl': '6.25rem'
    },

    weight: {
        primary: {
            hairline: '300',
            thin: '300',
            light: '300',
            normal: '300',
            medium: '500',
            semibold: '500',
            bold: '500',
            extrabold: '500',
            black: '500'
        },
        secondary: {
            hairline: '400',
            thin: '400',
            light: '400',
            normal: '400',
            medium: '400',
            semibold: '400',
            bold: '400',
            extrabold: '400',
            black: '400'
        }
    }

};

/**
 * Line heights
 * ==================
 * Line heights should be described in a context-relative size.
 * Eg. no unit or in 'em'.
 *
 * https://gitlab.digitalnatives.nl/toolkit/css-variables/-/blob/master/themes/default/typography/lineHeight.js
 */

const lineHeight = {
    ...defaultTheme.lineHeight,

    xs: '.9',
    sm: '1',
    md: '1.2',
    lg: '1.4',
    xl: '2'
};

/**
 * This is the default theme used in your application.
 * It will be applied to some toolkit components so they will closely match your project styles without having to style them by hand.
 * For an up-to-date list of all theme variables check out
 */
export default {
    color,
    transition,
    spacing,
    grid,
    borderRadius: {},
    boxShadow,
    font,
    lineHeight
};
